/**
 * Route guard.
 *
 * Convenience, not security: it decides what to render, and the backend
 * decides what is allowed. Someone who edits this away in devtools reaches an
 * app whose every call still fails server-side.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { homeFor } from './home';
import { useAuth } from './useAuth';
import { rememberDestination } from './returnTo';
import { Spinner } from '../app/Spinner';
import { MfaGate } from '../features/console/MfaGate';

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, session } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner label="Checking your session" />;

  if (!session) {
    /**
     * Remember where they were headed so sign-in can return them there
     * instead of dumping everyone on the dashboard.
     *
     * TWICE, because passwordless sign-in leaves the application. Router
     * state survives typing an address into the form on the next screen; it
     * does not survive going to an inbox and coming back through
     * `/signin/{token}`, which is how everybody who clicks a reminder
     * actually arrives. `rememberDestination` is the copy that survives, and
     * it never leaves this browser. See auth/returnTo.ts.
     */
    rememberDestination(location.pathname + location.search);
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  }

  // Authenticated with no membership. Every query would return nothing and
  // every page would render an empty shell, which reads as a broken product
  // rather than as an account that has not been set up yet.
  if (!session.orgId && location.pathname !== '/no-access') {
    // A system administrator has no membership by design; their place is
    // the console, not a page telling them they have no access.
    return <Navigate to={homeFor(session) === '/console' ? '/console' : '/no-access'} replace />;
  }

  // A system administrator account reaches every customer's data, so it
  // needs Google Authenticator wherever it signs in — the main site as much
  // as the console. Not enrolled yet (a new administrator, or one promoted
  // from an ordinary role) means enrolment first; enrolled means the code,
  // once per sign-in. The backend refuses sysadmin actions without it
  // regardless; this makes the requirement visible instead of a refusal.
  if (session.roles.includes('sysadmin')) {
    return <MfaGate>{children}</MfaGate>;
  }

  return <>{children}</>;
}
